import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';

import { useSmartSend } from '../../contexts/SmartSendContext';
import styles from '../../styles/DatePickerModal.module.css';
import {
  getTodayInTimezone,
  formatDateForInput,
  parseDateInput,
  isSameDay,
} from '../../utils/deliveryDateUtils';
import { isDevelopment } from '../../env/config';

/**
 * DatePickerModal Component - Enhanced date picker with visual disabled date styling
 *
 * Features:
 * - Uses react-datepicker for full calendar UI control
 * - Visual styling of disabled dates (greyed out, crossed out, not clickable)
 * - Timezone-aware date calculations
 * - Proper date range validation
 * - Accessibility support (ARIA labels, keyboard navigation)
 * - Mobile responsive design
 * - Follows SmartSend v2 conventions (CSS modules, CSS variables)
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onDateSelect - Callback when date is selected (receives YYYY-MM-DD string)
 * @param {string} props.selectedDate - Currently selected date in YYYY-MM-DD format
 * @param {string} props.earliestDateStr - Earliest selectable date in YYYY-MM-DD format
 * @param {string} props.latestDateStr - Latest selectable date in YYYY-MM-DD format
 * @param {string} props.timezone - IANA timezone (e.g., "America/New_York")
 * @param {boolean} props.displayDeliveryTime - Whether to show time selection
 * @param {Array<string>} props.availableTimeSlots - Available time slots for selected date
 * @param {string} props.selectedTime - Currently selected time slot
 * @param {Function} props.onTimeSelect - Callback when time is selected
 * @param {number} props.activeOffset - Active offset for determining same-day time slots
 * @param {string} props.orderType - Order type ('gesture' or other) to determine instruction text
 * @param {Function} props.onSave - Optional callback when Save is clicked (receives {date, time})
 */
const DatePickerModal = ({
  isOpen,
  onClose,
  onDateSelect,
  selectedDate,
  earliestDateStr,
  latestDateStr,
  timezone,
  displayDeliveryTime = true,
  availableTimeSlots = [],
  selectedTime = '',
  onTimeSelect,
  activeOffset = 0,
  orderType = 'gesture',
  onSave,
}) => {
  // ============================================
  // CONTEXT
  // ============================================
  const { appMode: inApp } = useSmartSend();

  // ============================================
  // STATE
  // ============================================
  const [selectedDateObj, setSelectedDateObj] = useState(null);
  const [timeSelected, setTimeSelected] = useState(false);
  const [localSelectedTime, setLocalSelectedTime] = useState(selectedTime);

  // Ref to track previous active element for focus restoration
  const previousActiveElementRef = useRef(null);

  // Ref to store event handler for stable cleanup
  const handleEscapeRef = useRef(null);

  // Ref for the overlay element (used to find its scrollable ancestor)
  const overlayRef = useRef(null);

  // Ref to track if we've initialized the calendar date (prevent re-initialization on selectedDate changes)
  const hasInitializedRef = useRef(false);

  // ============================================
  // CALENDAR DATE RANGE
  // ============================================
  // Calculate minimum and maximum dates for calendar
  // These ensure past dates and dates beyond the allowed range are disabled
  const minDate = useMemo(() => {
    if (!earliestDateStr) {
      // Fallback: use today if no earliest date provided
      return getTodayInTimezone(timezone);
    }
    return parseDateInput(earliestDateStr);
  }, [earliestDateStr, timezone]);

  const maxDate = useMemo(() => {
    if (!latestDateStr) {
      // Fallback: use 7 days from today if no latest date provided
      const today = getTodayInTimezone(timezone);
      const sevenDaysLater = new Date(today);
      sevenDaysLater.setDate(sevenDaysLater.getDate() + 7);
      return sevenDaysLater;
    }
    return parseDateInput(latestDateStr);
  }, [latestDateStr, timezone]);

  // ============================================
  // INITIALIZE CALENDAR DATE FROM SELECTED DATE
  // ============================================
  // Handles modal initialization and edge cases
  // Only initialize when modal opens, not on every selectedDate change
  useEffect(() => {
    if (isOpen && !hasInitializedRef.current) {
      // Initialize calendar date from selectedDate or earliestDate
      let initialDate = null;

      // Validate and use selectedDate if provided
      if (selectedDate) {
        // Validate date format
        if (!selectedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          if (isDevelopment()) {
            console.warn('Invalid selectedDate format:', selectedDate);
          }
          // Fall through to use minDate
        } else {
          try {
            const dateObj = parseDateInput(selectedDate);
            // Validate parsed date is valid
            if (!isNaN(dateObj.getTime())) {
              // Validate initial date is within range
              if (earliestDateStr && latestDateStr) {
                const minDateObj = parseDateInput(earliestDateStr);
                const maxDateObj = parseDateInput(latestDateStr);
                if (dateObj < minDateObj || dateObj > maxDateObj) {
                  if (isDevelopment()) {
                    console.warn(
                      'Selected date outside valid range, using minDate:',
                      {
                        selectedDate,
                        earliestDateStr,
                        latestDateStr,
                      }
                    );
                  }
                  initialDate = minDate;
                } else {
                  initialDate = dateObj;
                }
              } else {
                initialDate = dateObj;
              }
            } else {
              if (isDevelopment()) {
                console.warn('Invalid parsed date:', dateObj);
              }
              initialDate = minDate;
            }
          } catch (error) {
            if (isDevelopment()) {
              console.warn('Error parsing selectedDate:', error);
            }
            initialDate = minDate;
          }
        }
      }

      // Fallback to minDate if no valid selectedDate
      if (!initialDate && minDate) {
        initialDate = minDate;
      }

      setSelectedDateObj(initialDate);

      // Map "within-2-hours" back to "before-noon" for display if it's before 10am
      // This handles the case where user previously selected "within-2-hours"
      // but we want to show "Before Noon" in the UI if applicable
      let displayTime = selectedTime || '';
      if (displayTime === 'within-2-hours' && initialDate) {
        try {
          const today = getTodayInTimezone(timezone);
          if (isSameDay(initialDate, today)) {
            // Check if before 10am
            const now = new Date();
            const tenAM = new Date(
              today.getFullYear(),
              today.getMonth(),
              today.getDate(),
              10,
              0,
              0
            );
            if (now < tenAM) {
              displayTime = 'before-noon';
            }
          }
        } catch (error) {
          if (isDevelopment()) {
            console.warn('Error processing time display:', error);
          }
        }
      }

      setLocalSelectedTime(displayTime);
      setTimeSelected(!!displayTime);

      // Store previous active element for focus restoration
      previousActiveElementRef.current = document.activeElement;

      // Mark as initialized
      hasInitializedRef.current = true;
    } else if (!isOpen) {
      // Reset state when modal closes
      setSelectedDateObj(null);
      setLocalSelectedTime('');
      setTimeSelected(false);
      hasInitializedRef.current = false;
    }
  }, [
    isOpen,
    selectedDate,
    selectedTime,
    minDate,
    earliestDateStr,
    latestDateStr,
    timezone,
  ]);

  // ============================================
  // HANDLE DATE SELECTION
  // ============================================
  // Validates date selection and handles edge cases
  const handleDateChange = useCallback(
    (date) => {
      if (!date) {
        setSelectedDateObj(null);
        setLocalSelectedTime('');
        setTimeSelected(false);
        return;
      }

      // Validate date is a Date object
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        if (isDevelopment()) {
          console.warn('Invalid date object:', date);
        }
        toast.error('Invalid date selected. Please try again.');
        return;
      }

      // Validate date is within range (defensive check - library should prevent this, but we validate anyway)
      if (earliestDateStr && latestDateStr) {
        const minDateObj = parseDateInput(earliestDateStr);
        const maxDateObj = parseDateInput(latestDateStr);

        if (date < minDateObj || date > maxDateObj) {
          toast.error(
            `Please select a date between ${earliestDateStr} and ${latestDateStr}`
          );
          return;
        }
      }

      setSelectedDateObj(date);

      // Clear time selection when date changes (force re-selection)
      setLocalSelectedTime('');
      setTimeSelected(false);

      // Don't auto-select date - user must click "Save" to confirm selection
      // This provides consistent UX across all SKU types
    },
    [earliestDateStr, latestDateStr]
  );

  // ============================================
  // HANDLE TIME SELECTION
  // ============================================
  // When time is selected, validate it matches the selected date
  const handleTimeSelect = useCallback(
    (timeSlot) => {
      // If this is a "today" time slot, validate that the selected date is today
      if (timeSlot === 'within-2-hours' || timeSlot === 'before-noon') {
        const today = getTodayInTimezone(timezone);

        // Check if currently selected date is today
        const currentIsToday = selectedDateObj
          ? isSameDay(selectedDateObj, today)
          : false;

        // If a date is selected but it's not today, show error and don't allow the time selection
        if (selectedDateObj && !currentIsToday) {
          toast.error(
            "Within 2 hours is only available for today. Please select today's date or choose a different time slot."
          );
          return; // Don't update the time selection
        }

        // If no date is selected, set to today (only if today is within range)
        if (!selectedDateObj) {
          // Validate today is within range
          if (earliestDateStr && latestDateStr) {
            const minDateObj = parseDateInput(earliestDateStr);
            const maxDateObj = parseDateInput(latestDateStr);

            if (today >= minDateObj && today <= maxDateObj) {
              setSelectedDateObj(today);
            } else {
              // Today is outside range, show error
              toast.error(
                `Today is not within the allowed date range. Please select a date between ${earliestDateStr} and ${latestDateStr}`
              );
              return;
            }
          } else {
            setSelectedDateObj(today);
          }
        }
      }

      setLocalSelectedTime(timeSlot);
      setTimeSelected(true);

      // Don't call onTimeSelect here - wait for user to click "Save"
      // This allows users to change their time selection before saving
    },
    [
      selectedDateObj,
      timezone,
      earliestDateStr,
      latestDateStr,
    ]
  );

  // ============================================
  // CHECK IF DATE IS TODAY
  // ============================================
  const isDateToday = useCallback(
    (dateObj) => {
      if (!dateObj || !(dateObj instanceof Date)) return false;
      const today = getTodayInTimezone(timezone);
      return isSameDay(dateObj, today);
    },
    [timezone]
  );

  // ============================================
  // CHECK IF BEFORE 10AM
  // ============================================
  const isBefore10am = useCallback(
    (dateObj) => {
      if (!dateObj || !(dateObj instanceof Date)) return false;
      const today = getTodayInTimezone(timezone);

      if (!isSameDay(dateObj, today)) {
        return false;
      }

      const now = new Date();
      const tenAM = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        10,
        0,
        0
      );

      return now < tenAM;
    },
    [timezone]
  );

  // ============================================
  // CHECK IF SAVE BUTTON SHOULD BE DISABLED
  // ============================================
  // Button is disabled until minimum required selections are made:
  // - For gesture SKUs: both date and time must be selected
  // - For non-gesture SKUs: only date must be selected
  const isSaveDisabled = useMemo(() => {
    // Date is always required
    if (!selectedDateObj) {
      return true;
    }

    // For gesture SKUs, time is also required
    if (displayDeliveryTime) {
      return !timeSelected && !localSelectedTime;
    }

    // For non-gesture SKUs, only date is required
    return false;
  }, [selectedDateObj, displayDeliveryTime, timeSelected, localSelectedTime]);

  // ============================================
  // HANDLE SAVE
  // ============================================
  // Validates all inputs before saving and handles edge cases
  const handleSave = useCallback(() => {
    // Validate date is selected
    if (!selectedDateObj) {
      toast.error('Please select a date.');
      return;
    }

    // Validate date is a Date object
    if (
      !(selectedDateObj instanceof Date) ||
      isNaN(selectedDateObj.getTime())
    ) {
      if (isDevelopment()) {
        console.warn('Invalid date object:', selectedDateObj);
      }
      toast.error('Invalid date selected. Please try again.');
      return;
    }

    const dateStr = formatDateForInput(selectedDateObj);

    // Validate conversion succeeded
    if (!dateStr || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      if (isDevelopment()) {
        console.warn('Failed to format date:', selectedDateObj);
      }
      toast.error('Invalid date format. Please try again.');
      return;
    }

    // Validate date is within range (defensive check - library should prevent this)
    if (earliestDateStr && latestDateStr) {
      const selectedDateObj = parseDateInput(dateStr);
      const minDate = parseDateInput(earliestDateStr);
      const maxDate = parseDateInput(latestDateStr);

      // Validate parsed dates are valid
      if (isNaN(selectedDateObj.getTime())) {
        toast.error('Invalid date selected. Please try again.');
        return;
      }

      if (isNaN(minDate.getTime()) || isNaN(maxDate.getTime())) {
        if (isDevelopment()) {
          console.warn('Invalid date range:', {
            earliestDateStr,
            latestDateStr,
          });
        }
        toast.error('Invalid date range. Please refresh and try again.');
        return;
      }

      if (selectedDateObj < minDate || selectedDateObj > maxDate) {
        toast.error(
          `Please select a date between ${earliestDateStr} and ${latestDateStr}`
        );
        return;
      }
    }

    // If time is required, validate it's selected
    if (displayDeliveryTime && !timeSelected && !localSelectedTime) {
      toast.error('Please select a delivery time.');
      return;
    }

    // Validate time slot if provided
    if (displayDeliveryTime && localSelectedTime) {
      const validTimeSlots = [
        '10am-2pm',
        '2pm-8pm',
        'within-2-hours',
        'before-noon',
      ];
      if (!validTimeSlots.includes(localSelectedTime)) {
        if (isDevelopment()) {
          console.warn('Invalid time slot:', localSelectedTime);
        }
        toast.error('Invalid time slot selected. Please try again.');
        return;
      }
    }

    // Map "before-noon" to "within-2-hours" for backend compatibility
    // The UI shows "Before Noon" but backend expects "within-2-hours"
    const timeSlotValue =
      displayDeliveryTime && localSelectedTime && localSelectedTime === 'before-noon'
        ? 'within-2-hours'
        : localSelectedTime;

    // Call onSave callback first (if provided) with both date and time
    // This allows parent to track the selection as a single event
    if (onSave) {
      onSave({
        date: dateStr,
        time: displayDeliveryTime ? timeSlotValue || null : null,
      });
    }

    // All validations passed - call callbacks
    if (onDateSelect) {
      onDateSelect(dateStr);
    }

    if (displayDeliveryTime && onTimeSelect && timeSlotValue) {
      onTimeSelect(timeSlotValue);
    }

    // Close modal
    onClose();
  }, [
    selectedDateObj,
    timeSelected,
    localSelectedTime,
    displayDeliveryTime,
    earliestDateStr,
    latestDateStr,
    onDateSelect,
    onTimeSelect,
    onSave,
    onClose,
  ]);

  // ============================================
  // ACCESSIBILITY: FOCUS TRAPPING AND ESCAPE KEY
  // ============================================
  useEffect(() => {
    // Only set up listeners when modal is open
    if (!isOpen) {
      return;
    }

    // Lock background scroll while modal is open.
    // Walk up from the overlay to find the nearest scrollable ancestor
    // (the .contentWrapper in SmartSend) and disable its scroll.
    let scrollableParent = null;
    if (overlayRef.current) {
      let el = overlayRef.current.parentElement;
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          scrollableParent = el;
          break;
        }
        el = el.parentElement;
      }
    }
    const originalOverflowY = scrollableParent?.style.overflowY ?? '';
    if (scrollableParent) {
      scrollableParent.style.overflowY = 'hidden';
    }

    // Focus first focusable element when modal opens
    let modalContent = null;
    let firstFocusable = null;
    try {
      modalContent = document?.querySelector?.(`.${styles.modalContent}`);
      firstFocusable = modalContent?.querySelector?.(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
    } catch (error) {
      // Ignore querySelector errors
    }

    if (firstFocusable) {
      try {
        firstFocusable.focus();
      } catch (error) {
        // Ignore focus errors
      }
    }

    // Handle Escape key
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && onClose) {
        onClose();
      }
    };

    // Store handler in ref for stable cleanup
    handleEscapeRef.current = handleEscape;

    // Add event listener safely
    try {
      if (typeof document !== 'undefined' && document?.addEventListener) {
        document.addEventListener('keydown', handleEscape);
      }
    } catch (error) {
      if (isDevelopment()) {
        console.warn('Error adding event listeners:', error);
      }
    }

    // Cleanup function
    return () => {
      // Restore background scroll
      if (scrollableParent) {
        scrollableParent.style.overflowY = originalOverflowY;
      }

      // Safely remove event listeners using stored refs
      try {
        if (typeof document !== 'undefined' && document?.removeEventListener) {
          if (handleEscapeRef.current) {
            document.removeEventListener('keydown', handleEscapeRef.current);
            handleEscapeRef.current = null;
          }
        }
      } catch (error) {
        // Ignore cleanup errors - document may be unavailable
        if (isDevelopment()) {
          console.warn('Error removing event listeners:', error);
        }
      }

      // Restore focus to previous element
      if (previousActiveElementRef.current) {
        const previousElement = previousActiveElementRef.current;
        try {
          if (
            typeof document !== 'undefined' &&
            document?.body &&
            document.body.contains?.(previousElement) &&
            typeof previousElement?.focus === 'function'
          ) {
            setTimeout(() => {
              try {
                previousElement.focus();
              } catch (error) {
                // Element may no longer be in DOM, ignore
                if (isDevelopment()) {
                  console.warn('Could not restore focus:', error);
                }
              }
            }, 0);
          }
        } catch (error) {
          // Ignore focus restoration errors
          if (isDevelopment()) {
            console.warn('Error restoring focus:', error);
          }
        }
      }
    };
  }, [isOpen, onClose]);

  // ============================================
  // PREVENT BODY SCROLL WHEN MODAL IS OPEN
  // ============================================
  useEffect(() => {
    if (isOpen) {
      if (document && document.body) {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
          if (document && document.body) {
            document.body.style.overflow = originalOverflow;
          }
        };
      }
    }
  }, [isOpen]);

  // ============================================
  // RENDER
  // ============================================
  // Early return if modal is not open - this prevents Calendar from trying to clean up
  if (!isOpen) {
    return null;
  }

  const dateIsToday = selectedDateObj ? isDateToday(selectedDateObj) : false;

  // Debug: Log if dates are not available
  if (isDevelopment() && (!minDate || !maxDate)) {
    console.warn('DatePickerModal: minDate or maxDate is missing', {
      minDate,
      maxDate,
      earliestDateStr,
      latestDateStr,
    });
  }

  // Format date for display
  const formatDateDisplay = (dateObj) => {
    if (!dateObj || !(dateObj instanceof Date)) return '';
    return dateObj.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Handle click outside modal (on overlay backdrop)
  const handleClickOutside = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div
      ref={overlayRef}
      className={`${styles.modalOverlay}${inApp ? ` ${styles.inApp}` : ''}`}
      onClick={handleClickOutside}
      role="dialog"
      aria-modal="true"
      aria-labelledby="date-picker-title"
      aria-describedby="date-picker-description"
    >
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title} id="date-picker-title">
            {orderType === 'gesture'
              ? displayDeliveryTime
                ? 'Select a Delivery Date & Time'
                : 'Select a Delivery Date'
              : 'Select a Ship Date'}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onClose) {
                onClose();
              }
            }}
            aria-label="Close date picker"
          >
            ×
          </button>
        </div>

        {/* Note Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Note</h3>
          <p className={styles.sectionContent}>
            Gesture does our best to deliver within the selected date but cannot
            guarantee this 100% of the time.
          </p>
          <p className={styles.sectionContent}>
            Based on your selected delivery location, here are your earliest
            delivery options:
          </p>
        </div>

        {/* Calendar - Using react-datepicker */}
        {minDate && maxDate ? (
          <div className={styles.calendarSection}>
            <DatePicker
              selected={selectedDateObj}
              onChange={handleDateChange}
              minDate={minDate}
              maxDate={maxDate}
              inline
              className={styles.datePicker}
              dateFormat="MM/dd/yyyy"
            />
          </div>
        ) : (
          <div className={styles.calendarSection}>
            <div className={styles.errorMessage}>
              Unable to load calendar. Please refresh and try again.
              {isDevelopment() && (
                <div className={styles.debugMessage}>
                  Debug: minDate={minDate?.toString()}, maxDate=
                  {maxDate?.toString()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Time Selection - Appears below calendar after date is selected */}
        {displayDeliveryTime && selectedDateObj && (
          <div className={styles.section}>
            <h3 className={`${styles.sectionTitle} ${styles.timeSectionTitle}`}>
              Select Delivery Time
            </h3>

            {/* Time Options */}
            {!dateIsToday ? (
              <div className={styles.timeOptionsContainer}>
                <button
                  type="button"
                  className={`${styles.timeOptionButton} ${
                    localSelectedTime === '10am-2pm'
                      ? styles.timeOptionButtonActive
                      : ''
                  }`}
                  onClick={() => handleTimeSelect('10am-2pm')}
                  aria-pressed={localSelectedTime === '10am-2pm'}
                >
                  <p className={styles.selectedDate}>
                    {formatDateDisplay(selectedDateObj)}
                  </p>
                  <p className={styles.selectedDate}>10am – 2pm</p>
                </button>
                <button
                  type="button"
                  className={`${styles.timeOptionButton} ${
                    localSelectedTime === '2pm-8pm'
                      ? styles.timeOptionButtonActive
                      : ''
                  }`}
                  onClick={() => handleTimeSelect('2pm-8pm')}
                  aria-pressed={localSelectedTime === '2pm-8pm'}
                >
                  <p className={styles.selectedDate}>
                    {formatDateDisplay(selectedDateObj)}
                  </p>
                  <p className={styles.selectedDate}>2pm – 8pm</p>
                </button>
              </div>
            ) : (
              <div className={styles.timeOptionsContainer}>
                {isBefore10am(selectedDateObj) ? (
                  <button
                    type="button"
                    className={`${styles.timeOptionButton} ${
                      localSelectedTime === 'before-noon'
                        ? styles.timeOptionButtonActive
                        : ''
                    }`}
                    onClick={() => handleTimeSelect('before-noon')}
                    aria-pressed={localSelectedTime === 'before-noon'}
                  >
                    <p className={styles.selectedDate}>Today</p>
                    <p className={styles.selectedDate}>Before Noon</p>
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`${styles.timeOptionButton} ${
                      localSelectedTime === 'within-2-hours'
                        ? styles.timeOptionButtonActive
                        : ''
                    }`}
                    onClick={() => handleTimeSelect('within-2-hours')}
                    aria-pressed={localSelectedTime === 'within-2-hours'}
                  >
                    <p className={styles.selectedDate}>Today</p>
                    <p className={styles.selectedDate}>Within 2 hours</p>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Save Button */}
        <div className={styles.saveButtonContainer}>
          <button
            type="button"
            className={styles.saveButton}
            disabled={isSaveDisabled}
            onClick={handleSave}
            aria-label="Save selected date and time"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

DatePickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDateSelect: PropTypes.func.isRequired,
  selectedDate: PropTypes.string,
  earliestDateStr: PropTypes.string.isRequired,
  latestDateStr: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  displayDeliveryTime: PropTypes.bool,
  availableTimeSlots: PropTypes.arrayOf(PropTypes.string),
  selectedTime: PropTypes.string,
  onTimeSelect: PropTypes.func,
  activeOffset: PropTypes.number,
  orderType: PropTypes.string,
  onSave: PropTypes.func,
};

export default DatePickerModal;
